import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  IndianRupee,
  Calendar,
  TrendingUp,
  CheckCircle,
  Clock,
  FileText,
  CreditCard,
  AlertCircle,
  Mail,
  Phone,
  Users,
  Edit,
  Download,
  History,
  Lock,
  AlertTriangle,
  Banknote,
  XCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { formatCurrency, formatDate, getStatusColor } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function LoanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [dateType, setDateType] = useState('application'); // 'application' or 'end'

  // Loan Foreclosure State
  const [isForeclosureOpen, setIsForeclosureOpen] = useState(false);
  const [foreclosureStep, setForeclosureStep] = useState(1); // 1: Review, 2: Payment, 3: Confirm
  const [foreclosureData, setForeclosureData] = useState({
    paymentMode: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    confirmCheckbox: false
  });
  const [closingLoan, setClosingLoan] = useState(false);

  // EMI Payment Modal State
  const [isEMIPaymentOpen, setIsEMIPaymentOpen] = useState(false);
  const [emiPaymentData, setEmiPaymentData] = useState({
    amount: 0,
    paymentMode: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [recordingPayment, setRecordingPayment] = useState(false);

  useEffect(() => {
    fetchLoanDetails();
  }, [id]);

  const handleReschedule = async () => {
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      setLoading(true);
      // We are reusing the reschedule endpoint which now updates loan_application_date
      // If dateType is 'application', we call reschedule
      // If dateType is 'end', we might need a new endpoint or just ignore for now as user didn't ask to edit end date explicitly via UI action, just display.
      // But wait, the user said "make sure update in database / supabase first and then sync to frontend".
      // The user wants to SEE the dates.

      if (dateType === 'application') {
        await api.loans.reschedule(id, { new_start_date: newDate });
        toast.success('Loan Application Date updated successfully');
      }

      setIsRescheduleOpen(false);
      fetchLoanDetails();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update date');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAgreement = async () => {
    try {
      const response = await api.loans.downloadAgreement(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Loan_Agreement_${loan.loan_account_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download agreement');
    }
  };

  // ===== LOAN FORECLOSURE HANDLERS =====
  const openForeclosureModal = () => {
    setForeclosureStep(1);
    setForeclosureData({
      paymentMode: 'cash',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: '',
      confirmCheckbox: false
    });
    setIsForeclosureOpen(true);
  };

  const handleForeclosureNext = () => {
    if (foreclosureStep < 3) {
      setForeclosureStep(foreclosureStep + 1);
    }
  };

  const handleForeclosurePrev = () => {
    if (foreclosureStep > 1) {
      setForeclosureStep(foreclosureStep - 1);
    }
  };

  const handleForeclosureSubmit = async () => {
    if (!foreclosureData.confirmCheckbox) {
      toast.error('Please confirm that you want to close this loan');
      return;
    }

    // Final confirmation alert
    const confirmed = window.confirm(
      `⚠️ FINAL CONFIRMATION\n\nYou are about to close this loan and mark it as PAID IN FULL.\n\nAmount: ${formatCurrency(stats.outstanding)}\nPayment Mode: ${foreclosureData.paymentMode.toUpperCase()}\nDate: ${foreclosureData.paymentDate}\n\nThis action CANNOT be undone. Continue?`
    );

    if (!confirmed) return;

    try {
      setClosingLoan(true);
      await api.loans.closeEarly(id, {
        payment_date: foreclosureData.paymentDate,
        payment_mode: foreclosureData.paymentMode,
        notes: foreclosureData.notes || `Loan foreclosed by manager on ${foreclosureData.paymentDate}`
      });

      toast.success('🎉 Loan closed successfully! All pending EMIs have been marked as paid.');
      setIsForeclosureOpen(false);

      // Refresh loan details to show updated status
      fetchLoanDetails();
    } catch (error) {
      console.error('Foreclosure error:', error);
      toast.error(error.response?.data?.detail || 'Failed to close loan. Please try again.');
    } finally {
      setClosingLoan(false);
    }
  };
  // ===== END FORECLOSURE HANDLERS =====

  // ===== EMI PAYMENT HANDLERS =====
  const openEMIPaymentModal = () => {
    // Pre-fill with next EMI amount
    const nextEMI = schedule.find(emi =>
      emi.payment_status === 'pending' ||
      emi.payment_status === 'partial' ||
      emi.payment_status === 'unpaid'
    );
    const emiAmount = nextEMI ? parseFloat(nextEMI.emi_amount) : (stats.calculatedEMI || 0);

    setEmiPaymentData({
      amount: emiAmount,
      paymentMode: 'cash',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setIsEMIPaymentOpen(true);
  };

  const handleEMIPaymentSubmit = async () => {
    if (!emiPaymentData.amount || emiPaymentData.amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setRecordingPayment(true);
      await api.loans.recordPayment(id, {
        amount: emiPaymentData.amount,
        payment_mode: emiPaymentData.paymentMode,
        payment_date: emiPaymentData.paymentDate,
        notes: emiPaymentData.notes || `EMI payment recorded from Loan Details page`
      });

      toast.success(`✅ Payment of ${formatCurrency(emiPaymentData.amount)} recorded successfully!`);
      setIsEMIPaymentOpen(false);

      // Refresh loan details to show updated balance
      fetchLoanDetails();
    } catch (error) {
      console.error('EMI Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment. Please try again.');
    } finally {
      setRecordingPayment(false);
    }
  };
  // ===== END EMI PAYMENT HANDLERS =====

  const fetchLoanDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch loan details
      let loanData = await api.loans.getOne(id);

      // If account_holder_name is missing but we have account_holder_id, fetch the member
      if (!loanData.account_holder_name && loanData.account_holder_id) {
        try {
          const memberData = await api.accountHolders.getOne(loanData.account_holder_id);
          loanData = {
            ...loanData,
            account_holder_name: formatFullName(memberData.first_name, memberData.middle_name, memberData.last_name),
            account_holder: memberData
          };
        } catch (err) {
          console.error('Failed to fetch member details', err);
          loanData.account_holder_name = 'Unknown Member';
        }
      }

      setLoan(loanData);

      // Fetch schedule
      const scheduleData = await api.loans.getSchedule(id);
      setSchedule(scheduleData);

      // Fetch payments
      try {
        const paymentsData = await api.loans.getPayments(id);
        setPayments(paymentsData || []);
      } catch (err) {
        // Payments might not exist yet
        setPayments([]);
      }

    } catch (error) {
      console.error('Error fetching loan details:', error);
      setError(error.response?.data?.detail || 'Failed to load loan details');
      toast.error('Failed to load loan details');
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived values
  const calculateStats = () => {
    if (!loan) return {};

    // FIX: Use database values directly instead of calculating from schedule
    // These values come from the database and are 100% accurate
    const totalPayableAmount = loan.principal_amount;
    const calculatedEMI = Math.round(totalPayableAmount / loan.tenure_months);

    // FIX #1: Outstanding Balance - use outstanding_principal from database
    const outstanding = loan.outstanding_principal || 0;

    // FIX #2: Total Paid - calculate from principal - outstanding
    const totalPaid = loan.principal_amount - (loan.outstanding_principal || 0);

    // FIX #3: EMIs Paid - calculate from tenure - outstanding_installments
    const paidCount = loan.tenure_months - (loan.outstanding_installments || 0);
    const totalCount = loan.tenure_months;

    // Total Due is the full principal amount (interest is deducted upfront)
    const totalDue = totalPayableAmount;

    // Check if loan is completed
    const isLoanClosed = loan.status === 'closed' || outstanding === 0;

    // FIXED: Calculate Next Due Date from first PENDING EMI in schedule
    // This ensures advance payments correctly show next month's due date
    let nextDueDate = null;
    let nextDueAmount = calculatedEMI;

    if (!isLoanClosed && loan.outstanding_installments > 0 && schedule && schedule.length > 0) {
      // Find the first pending/partial EMI from schedule
      const nextPendingEMI = schedule.find(emi =>
        emi.payment_status === 'pending' ||
        emi.payment_status === 'partial' ||
        emi.payment_status === 'unpaid'
      );

      if (nextPendingEMI && nextPendingEMI.due_date) {
        nextDueDate = nextPendingEMI.due_date;
        nextDueAmount = parseFloat(nextPendingEMI.emi_amount) || calculatedEMI;
      }
    }

    // NEW LOGIC: Calculate Overdue EMIs
    let lastPaymentDate = null;
    let overdueCount = 0;
    let overdueAmount = 0;

    if (schedule && schedule.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison

      // Find last payment date
      const paidEMIs = schedule.filter(emi => emi.payment_status === 'paid' && emi.paid_date);
      if (paidEMIs.length > 0) {
        lastPaymentDate = paidEMIs[paidEMIs.length - 1].paid_date;
      }

      // Count overdue EMIs (unpaid EMIs with due_date < today)
      const overdueEMIs = schedule.filter(emi => {
        if (emi.payment_status !== 'unpaid') return false;
        const dueDate = new Date(emi.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      });

      overdueCount = overdueEMIs.length;
      overdueAmount = overdueEMIs.reduce((sum, emi) => sum + parseFloat(emi.emi_amount || 0), 0);
    }

    // Check if loan end date has passed and still has outstanding amount
    if (!isLoanClosed && loan.loan_end_date) {
      const today = new Date();
      const endDate = new Date(loan.loan_end_date);
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < today && outstanding > 0) {
        // Loan is overdue (end date passed but not paid off)
        if (loan.outstanding_installments > 0) {
          overdueCount = loan.outstanding_installments;
          overdueAmount = outstanding;
        }
      }
    }

    return {
      totalDue,
      totalPaid,
      outstanding,
      paidCount,
      totalCount,
      nextDueDate, // NEW: Dynamic calculation based on 20th of month
      nextDueAmount,
      lastPaymentDate,
      overdueCount, // NEW: Proper overdue detection
      overdueAmount, // NEW: Total overdue amount
      calculatedEMI,
      isLoanClosed // NEW: Flag to hide next due date for closed loans
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="text-gray-600">Loading loan details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !loan) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800">Loan Not Found</h2>
            <p className="text-gray-600">{error || 'The loan you are looking for does not exist.'}</p>
            <Button onClick={() => navigate('/manager/loans')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Loans
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/manager/loans')}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                Back
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAgreement}
                className="hover:bg-gray-100 text-blue-600 border-blue-200"
              >
                <Download className="w-4 h-4 mr-1 sm:mr-2" />
                Agreement
              </Button>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-1 gradient-text">
                Loan Details
              </h1>
              <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span className="truncate max-w-[180px] sm:max-w-none" title={loan.loan_account_number}>
                  Account: {loan.loan_account_number}
                </span>
              </p>
            </div>
          </div>
          <Badge className={`${getStatusColor(loan.status)} text-base sm:text-lg px-3 sm:px-4 py-1.5 sm:py-2 self-start sm:self-auto`}>
            {loan.status?.toUpperCase()}
          </Badge>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">

            {/* Borrower Information */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Borrower Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-3 sm:gap-4 mb-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg text-white font-bold text-lg md:text-2xl shrink-0">
                    {loan.account_holder_name ? loan.account_holder_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'LN'}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Name</p>
                      <p className="font-semibold text-lg">{loan.account_holder_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Member ID</p>
                      <p className="font-semibold">{loan.account_holder_account_number || 'N/A'}</p>
                    </div>
                    {loan.account_holder?.contact_number && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          Contact
                        </p>
                        <p className="font-semibold">{loan.account_holder.contact_number}</p>
                      </div>
                    )}
                    {loan.account_holder?.email && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          Email
                        </p>
                        <p className="font-semibold text-sm">{loan.account_holder.email}</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loan Summary */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                  Loan Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Principal Amount</p>
                    <p className="text-lg md:text-2xl font-bold text-emerald-600">
                      {formatCurrency(loan.principal_amount)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Monthly Interest Rate</p>
                    <p className="text-lg md:text-2xl font-bold text-blue-600">
                      {loan.monthly_interest_rate || 0.5}%
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Total Interest</p>
                    <p className="text-lg md:text-2xl font-bold text-orange-600">
                      - {formatCurrency(loan.principal_amount * ((loan.monthly_interest_rate || 0.5) / 100) * loan.tenure_months)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Approval Date</p>
                    <p className="text-xl font-bold">
                      {formatDate(loan.loan_application_date || loan.created_at)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Tenure</p>
                    <p className="text-xl font-bold">{loan.tenure_months} months</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">EMI Amount</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(schedule[0]?.emi_amount || stats.calculatedEMI || 0)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Disbursed Amount</p>
                    <p className="text-2xl font-bold text-teal-600">
                      {formatCurrency(loan.principal_amount - (loan.principal_amount * ((loan.monthly_interest_rate || 0.5) / 100) * loan.tenure_months))}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Total Payable</p>
                    <p className="text-lg md:text-2xl font-bold text-purple-600">
                      {formatCurrency(stats.totalDue)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Outstanding</p>
                    <p className="text-lg md:text-2xl font-bold text-red-600">
                      {formatCurrency(stats.outstanding)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guarantor Information */}
            {(loan.guarantor1_name || loan.guarantor2_name) && (
              <Card className="shadow-sm border-orange-100">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" />
                    Guarantor Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Guarantor 1 */}
                    {loan.guarantor1_name && (
                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                          <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                          Guarantor 1
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{loan.guarantor1_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">{loan.guarantor1_phone}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">ID Number:</span>
                            <span className="font-medium">{loan.guarantor1_id_number}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Relationship:</span>
                            <span className="font-medium">{loan.guarantor1_relationship}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Guarantor 2 */}
                    {loan.guarantor2_name && (
                      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                          <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                          Guarantor 2
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{loan.guarantor2_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">{loan.guarantor2_phone}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">ID Number:</span>
                            <span className="font-medium">{loan.guarantor2_id_number}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Relationship:</span>
                            <span className="font-medium">{loan.guarantor2_relationship}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audit Logs */}
            {loan.audit_logs && loan.audit_logs.length > 0 && (
              <Card className="glass shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-emerald-600" />
                    Audit Logs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {loan.audit_logs.map((log, index) => (
                      <div key={index} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                        <div className="mt-1">
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{log.action}</p>
                          <p className="text-xs text-gray-600">{log.details}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-400">{formatDate(log.date)}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-400">{log.user}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Progress */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Payment Progress
                </CardTitle>
                <CardDescription>
                  Track repayment status and upcoming EMIs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">EMIs Paid: {stats.paidCount} / {stats.totalCount}</span>
                    <span className="font-semibold text-emerald-600">
                      {stats.totalCount > 0 ? Math.round((stats.paidCount / stats.totalCount) * 100) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-green-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${stats.totalCount > 0 ? (stats.paidCount / stats.totalCount) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <p className="text-sm text-gray-600 mb-1">Total Paid</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(stats.totalPaid)}</p>
                  </div>

                  {stats.nextDueDate && !stats.isLoanClosed && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-sm text-gray-600 mb-1">Next Due Date</p>
                      <p className="text-lg font-bold text-blue-600">{formatDate(stats.nextDueDate)}</p>
                      <p className="text-sm text-gray-600">{formatCurrency(stats.nextDueAmount)}</p>
                    </div>
                  )}

                  {stats.lastPaymentDate && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                      <p className="text-sm text-gray-600 mb-1">Last Payment On</p>
                      <p className="text-lg font-bold text-purple-600">{formatDate(stats.lastPaymentDate)}</p>
                    </div>
                  )}

                  {stats.overdueCount > 0 && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                      <p className="text-sm text-gray-600 mb-1">Overdue EMIs</p>
                      <p className="text-xl font-bold text-red-600">{stats.overdueCount}</p>
                      <p className="text-sm text-red-600">{formatCurrency(stats.overdueAmount)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* EMI Schedule / Payment History */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-emerald-600" />
                  EMI Schedule & Payment History
                </CardTitle>
                <CardDescription>
                  Complete EMI schedule with payment status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {schedule && schedule.length > 0 ? (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                          <tr>
                            <th className="px-3 py-3 text-left">#</th>
                            <th className="px-3 py-3 text-left">Due Date</th>
                            <th className="px-3 py-3 text-right">EMI</th>
                            <th className="px-3 py-3 text-left">Paid Date</th>
                            <th className="px-3 py-3 text-right">Paid</th>
                            <th className="px-3 py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {schedule.map((emi, index) => {
                            const isPaid = emi.payment_status === 'paid';
                            const isOverdue = !isPaid && new Date(emi.due_date) < new Date();
                            const isPartial = emi.payment_status === 'partial';

                            return (
                              <tr key={emi.id || index} className={`${isPaid ? 'bg-green-50/50' : isOverdue ? 'bg-red-50/50' : ''}`}>
                                <td className="px-3 py-2 font-medium text-gray-700">{emi.installment_no || index + 1}</td>
                                <td className="px-3 py-2 text-gray-600">{formatDate(emi.due_date)}</td>
                                <td className="px-3 py-2 text-right font-medium">{formatCurrency(emi.emi_amount)}</td>
                                <td className="px-3 py-2 text-gray-600">{emi.paid_date ? formatDate(emi.paid_date) : '-'}</td>
                                <td className="px-3 py-2 text-right font-medium text-green-600">
                                  {emi.paid_amount ? formatCurrency(emi.paid_amount) : '-'}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Badge className={
                                    isPaid ? 'bg-green-100 text-green-700' :
                                      isPartial ? 'bg-yellow-100 text-yellow-700' :
                                        isOverdue ? 'bg-red-100 text-red-700' :
                                          'bg-gray-100 text-gray-600'
                                  }>
                                    {isPaid ? 'Paid' : isPartial ? 'Partial' : isOverdue ? 'Overdue' : 'Pending'}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 border-t">
                        Showing {schedule.length} EMI{schedule.length !== 1 ? 's' : ''} • {stats.paidCount} paid, {stats.totalCount - stats.paidCount} remaining
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {schedule.map((emi, index) => {
                        const isPaid = emi.payment_status === 'paid';
                        const isOverdue = !isPaid && new Date(emi.due_date) < new Date();
                        const isPartial = emi.payment_status === 'partial';

                        return (
                          <Card key={emi.id || index} className={`p-4 ${isPaid ? 'bg-green-50/50' : isOverdue ? 'bg-red-50/50' : ''}`}>
                            {/* EMI Number & Status */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                                  {emi.installment_no || index + 1}
                                </div>
                                <span className="font-semibold text-gray-900">EMI #{emi.installment_no || index + 1}</span>
                              </div>
                              <Badge className={
                                isPaid ? 'bg-green-100 text-green-700' :
                                  isPartial ? 'bg-yellow-100 text-yellow-700' :
                                    isOverdue ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-600'
                              }>
                                {isPaid ? 'Paid' : isPartial ? 'Partial' : isOverdue ? 'Overdue' : 'Pending'}
                              </Badge>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Due Date</div>
                                <div className="text-sm font-medium text-gray-900">{formatDate(emi.due_date)}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Paid Date</div>
                                <div className="text-sm font-medium text-gray-900">
                                  {emi.paid_date ? formatDate(emi.paid_date) : <span className="text-gray-400">Not paid</span>}
                                </div>
                              </div>
                            </div>

                            {/* Amounts */}
                            <div className="grid grid-cols-2 gap-3 p-3 bg-white rounded-lg">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">EMI Amount</div>
                                <div className="text-base font-bold text-blue-600">{formatCurrency(emi.emi_amount)}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Paid Amount</div>
                                <div className="text-base font-bold text-green-600">
                                  {emi.paid_amount ? formatCurrency(emi.paid_amount) : <span className="text-gray-400">₹0</span>}
                                </div>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                      <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 rounded-lg text-center">
                        Showing {schedule.length} EMI{schedule.length !== 1 ? 's' : ''} • {stats.paidCount} paid, {stats.totalCount - stats.paidCount} remaining
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    <p>No EMI schedule available</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">

            {/* Approval Details */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Approval Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loan.approval_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Approval Date</p>
                    <p className="font-semibold">{formatDate(loan.approval_date)}</p>
                  </div>
                )}

                {loan.approved_by && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Approved By</p>
                    <p className="font-semibold">{loan.approved_by}</p>
                  </div>
                )}

                {loan.loan_application_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                      Application Date
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          setNewDate(loan.loan_application_date || '');
                          setDateType('application');
                          setIsRescheduleOpen(true);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    </p>
                    <p className="font-semibold">{formatDate(loan.loan_application_date)}</p>
                  </div>
                )}

                {loan.loan_end_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Loan End Date</p>
                    <p className="font-semibold">{formatDate(loan.loan_end_date)}</p>
                  </div>
                )}

                {loan.closure_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Closure Date</p>
                    <p className="font-semibold">{formatDate(loan.closure_date)}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="glass shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Record EMI Payment - Only show for active loans */}
                {loan.status === 'active' && stats.outstanding > 0 && (
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-md"
                    onClick={openEMIPaymentModal}
                  >
                    <IndianRupee className="w-4 h-4 mr-2" />
                    Record EMI Payment
                  </Button>
                )}

                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => window.print()}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Print Details
                </Button>

                {/* Close Loan Button - Only show for active loans with outstanding balance */}
                {loan.status === 'active' && stats.outstanding > 0 && (
                  <>
                    <Separator className="my-2" />
                    <Button
                      className="w-full bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-md"
                      onClick={openForeclosureModal}
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Close Loan (Pay Full)
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      Outstanding: {formatCurrency(stats.outstanding)}
                    </p>
                  </>
                )}

                {/* Show "Loan Closed" badge if already closed */}
                {loan.status === 'closed' && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-800">Loan Closed</p>
                    <p className="text-xs text-green-600">Fully Paid</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </div>

      </div>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Application Date</DialogTitle>
            <DialogDescription>
              Updating the application date will regenerate the loan schedule and update all future due dates.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label htmlFor="new-date" className="sm:text-right">
                New Date
              </Label>
              <Input
                id="new-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRescheduleOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={loading}>
              {loading ? 'Updating...' : 'Update Date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== LOAN FORECLOSURE MODAL ===== */}
      <Dialog open={isForeclosureOpen} onOpenChange={(open) => {
        if (!open) {
          setForeclosureStep(1);
          setForeclosureData({
            paymentMode: 'cash',
            paymentDate: new Date().toISOString().split('T')[0],
            notes: '',
            confirmCheckbox: false
          });
        }
        setIsForeclosureOpen(open);
      }}>
        <DialogContent className="w-[95vw] max-w-lg sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Lock className="w-5 h-5 text-red-600" />
              Close Loan - Step {foreclosureStep} of 3
            </DialogTitle>
            <DialogDescription>
              {foreclosureStep === 1 && "Review the outstanding amount breakdown"}
              {foreclosureStep === 2 && "Enter payment details"}
              {foreclosureStep === 3 && "Final confirmation before closing"}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${foreclosureStep === step
                  ? 'bg-red-600 text-white'
                  : foreclosureStep > step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                  }`}
              >
                {foreclosureStep > step ? '✓' : step}
              </div>
            ))}
          </div>

          <Separator />

          {/* Step 1: Outstanding Breakdown */}
          {foreclosureStep === 1 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Outstanding Amount Breakdown
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Loan Amount:</span>
                    <span className="font-medium">{formatCurrency(loan?.principal_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Paid:</span>
                    <span className="font-medium text-green-600">- {formatCurrency(stats.totalPaid)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-red-800">Outstanding Balance:</span>
                    <span className="font-bold text-red-600">{formatCurrency(stats.outstanding)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">EMI Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">EMIs Paid</p>
                    <p className="font-medium text-green-600">{stats.paidCount} of {stats.totalCount}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">EMIs Remaining</p>
                    <p className="font-medium text-red-600">{stats.totalCount - stats.paidCount}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Important Notice</p>
                    <p className="text-sm text-amber-700">
                      Closing this loan will mark all {stats.totalCount - stats.paidCount} pending EMIs as paid
                      and change the loan status to "CLOSED". This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Payment Details */}
          {foreclosureStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Amount to Collect:</span>
                  <span className="text-2xl font-bold text-red-600">{formatCurrency(stats.outstanding)}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentMode">Payment Mode</Label>
                  <div className="h-10 px-3 py-2 bg-gray-100 rounded-md border border-gray-200 text-gray-700 flex items-center gap-2 font-medium">
                    <Banknote className="w-4 h-4" /> Cash
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentDate">Payment Date *</Label>
                  <Input
                    id="paymentDate"
                    type="date"
                    value={foreclosureData.paymentDate}
                    onChange={(e) => setForeclosureData({ ...foreclosureData, paymentDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    placeholder="e.g., Early closure requested by member"
                    value={foreclosureData.notes}
                    onChange={(e) => setForeclosureData({ ...foreclosureData, notes: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Final Confirmation */}
          {foreclosureStep === 3 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-100 rounded-lg border-2 border-red-300">
                <h4 className="font-bold text-red-800 mb-3 flex items-center gap-2 text-lg">
                  <XCircle className="w-6 h-6" />
                  Final Confirmation Required
                </h4>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-1 border-b border-red-200">
                    <span className="text-gray-700">Member:</span>
                    <span className="font-medium">{loan?.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-red-200">
                    <span className="text-gray-700">Loan Account:</span>
                    <span className="font-medium">{loan?.loan_account_number}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-red-200">
                    <span className="text-gray-700">Closure Amount:</span>
                    <span className="font-bold text-red-600 text-lg">{formatCurrency(stats.outstanding)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-red-200">
                    <span className="text-gray-700">Payment Mode:</span>
                    <span className="font-medium capitalize">{foreclosureData.paymentMode}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-700">Payment Date:</span>
                    <span className="font-medium">{formatDate(foreclosureData.paymentDate)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-300">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800">⚠️ This action is IRREVERSIBLE</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Once confirmed, all pending EMIs will be marked as paid and the loan will be permanently closed.
                      Make sure you have collected the full outstanding amount of {formatCurrency(stats.outstanding)}.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border-2 border-gray-300">
                <Checkbox
                  id="confirmCheckbox"
                  checked={foreclosureData.confirmCheckbox}
                  onCheckedChange={(checked) => setForeclosureData({ ...foreclosureData, confirmCheckbox: checked })}
                  className="mt-1"
                />
                <Label htmlFor="confirmCheckbox" className="text-sm cursor-pointer">
                  <span className="font-semibold">I confirm</span> that I have collected the full outstanding amount of{' '}
                  <span className="font-bold text-red-600">{formatCurrency(stats.outstanding)}</span> from the member
                  and I want to close this loan permanently.
                </Label>
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between gap-2 sm:justify-between">
            <div>
              {foreclosureStep > 1 && (
                <Button variant="outline" onClick={handleForeclosurePrev} disabled={closingLoan}>
                  ← Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsForeclosureOpen(false)} disabled={closingLoan}>
                Cancel
              </Button>
              {foreclosureStep < 3 ? (
                <Button onClick={handleForeclosureNext} className="bg-red-600 hover:bg-red-700">
                  Next →
                </Button>
              ) : (
                <Button
                  onClick={handleForeclosureSubmit}
                  disabled={!foreclosureData.confirmCheckbox || closingLoan}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {closingLoan ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Closing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Close Loan
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ===== END FORECLOSURE MODAL ===== */}

      {/* ===== EMI PAYMENT MODAL ===== */}
      <Dialog open={isEMIPaymentOpen} onOpenChange={setIsEMIPaymentOpen}>
        <DialogContent className="w-[95vw] max-w-md sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
              Record EMI Payment
            </DialogTitle>
            <DialogDescription>
              Record payment for loan: {loan?.loan_account_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Next Due Info */}
            {stats.nextDueDate && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-700">Next EMI Due:</span>
                  <span className="font-semibold text-blue-800">{formatDate(stats.nextDueDate)}</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-blue-700">EMI Amount:</span>
                  <span className="font-bold text-blue-800">{formatCurrency(stats.nextDueAmount || stats.calculatedEMI)}</span>
                </div>
              </div>
            )}

            {/* Payment Amount */}
            <div className="space-y-2">
              <Label htmlFor="emiAmount">Payment Amount *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="emiAmount"
                  type="number"
                  value={emiPaymentData.amount}
                  onChange={(e) => setEmiPaymentData({ ...emiPaymentData, amount: parseFloat(e.target.value) || 0 })}
                  className="pl-9"
                  placeholder="Enter amount"
                />
              </div>
              <p className="text-xs text-gray-500">
                Outstanding: {formatCurrency(stats.outstanding)} | EMI: {formatCurrency(stats.calculatedEMI)}
              </p>
            </div>

            {/* Payment Mode */}
            <div className="space-y-2">
              <Label htmlFor="emiPaymentMode">Payment Mode</Label>
              <div className="h-10 px-3 py-2 bg-gray-100 rounded-md border border-gray-200 text-gray-700 flex items-center gap-2 font-medium">
                <Banknote className="w-4 h-4" /> Cash
              </div>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="emiPaymentDate">Payment Date *</Label>
              <Input
                id="emiPaymentDate"
                type="date"
                value={emiPaymentData.paymentDate}
                onChange={(e) => setEmiPaymentData({ ...emiPaymentData, paymentDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="emiNotes">Notes (Optional)</Label>
              <Input
                id="emiNotes"
                placeholder="e.g., December EMI payment"
                value={emiPaymentData.notes}
                onChange={(e) => setEmiPaymentData({ ...emiPaymentData, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEMIPaymentOpen(false)} disabled={recordingPayment}>
              Cancel
            </Button>
            <Button
              onClick={handleEMIPaymentSubmit}
              disabled={!emiPaymentData.amount || recordingPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {recordingPayment ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Recording...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ===== END EMI PAYMENT MODAL ===== */}
    </DashboardLayout>
  );
}

export default LoanDetails;
